import { useState, useRef, useCallback, useEffect } from 'react'
import { Card, CardContent, Typography, Box, Dialog, IconButton, ButtonBase } from '@mui/material'

// Sample Images card: one-at-a-time carousel (matching the patient-facing
// results view) + click-to-enlarge lightbox.
// Signed S3 URLs expire after 30 minutes (see mfh-api/utils/kitResultImages.js) —
// a provider is more likely than a patient to leave this page open that long,
// so a broken image triggers one refetch of fresh URLs via onExpired before
// falling back to a permanent "unavailable" placeholder.
const SampleImageGrid = ({ images, loadError, onExpired }) => {
  const list = images || []
  const [activeIndex, setActiveIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [brokenKeys, setBrokenKeys] = useState(() => new Set())
  const retriedKeysRef = useRef(new Set())

  // A new images array means either a different kit (prev/next nav) or a
  // fresh set of signed URLs after an expiry refetch — either way, start
  // back at the first image rather than an index that may no longer apply.
  useEffect(() => {
    setActiveIndex(0)
  }, [images])

  const handleImageError = useCallback(
    (key) => {
      if (retriedKeysRef.current.has(key)) {
        setBrokenKeys((prev) => new Set(prev).add(key))
        return
      }
      retriedKeysRef.current.add(key)
      onExpired?.()
    },
    [onExpired]
  )

  const showPrev = useCallback(() => setActiveIndex((i) => (i > 0 ? i - 1 : i)), [])
  const showNext = useCallback(() => setActiveIndex((i) => (i < list.length - 1 ? i + 1 : i)), [list.length])

  useEffect(() => {
    if (!lightboxOpen) return undefined
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') showPrev()
      if (e.key === 'ArrowRight') showNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, showPrev, showNext])

  if (list.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Sample Images
          </Typography>
          <Typography variant="body2" color={loadError ? 'error' : 'text.secondary'}>
            {loadError ? 'Sample images could not be loaded — try refreshing the page.' : 'No sample images available'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // Clamp rather than trust activeIndex: `images` can shrink (kit nav, or a
  // fresh signed-URL set after expiry) in the same render the reset effect
  // above hasn't fired for yet, and an index the size of the old list would
  // read past the end of the new one.
  const safeIndex = Math.min(activeIndex, list.length - 1)
  const current = list[safeIndex]
  const isBroken = brokenKeys.has(current.key)
  const hasMultiple = list.length > 1

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Sample Images
          </Typography>
          {hasMultiple && (
            <Typography variant="body2" color="text.secondary">
              {safeIndex + 1} of {list.length}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasMultiple && (
            <IconButton
              onClick={showPrev}
              disabled={safeIndex === 0}
              aria-label="Previous image"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <span className="fa-solid fa-chevron-left"></span>
            </IconButton>
          )}

          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            {isBroken ? (
              <Box
                sx={{
                  height: 360,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgb(240,240,240)',
                  borderRadius: 1,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">Image unavailable</Typography>
              </Box>
            ) : (
              <ButtonBase
                onClick={() => setLightboxOpen(true)}
                aria-label={`Enlarge sample image ${safeIndex + 1}${current.caption ? `: ${current.caption}` : ''}`}
                sx={{ display: 'block', width: '100%', minHeight: 44, borderRadius: 1 }}
              >
                <Box
                  component="img"
                  src={current.url}
                  alt={current.caption || `Sample image ${safeIndex + 1}`}
                  // Only one of the grid/lightbox <img>s for this key should react to
                  // a load failure at a time — otherwise both fire onError for the
                  // same URL and the second call sees the key already "retried",
                  // marking a recoverable expiry permanently broken. The lightbox
                  // image owns error handling while it's open.
                  onError={lightboxOpen ? undefined : () => handleImageError(current.key)}
                  sx={{
                    width: '100%',
                    maxHeight: 420,
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: '#000',
                    display: 'block',
                  }}
                />
              </ButtonBase>
            )}
            {(current.caption || current.magnification) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {[current.caption, current.magnification].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>

          {hasMultiple && (
            <IconButton
              onClick={showNext}
              disabled={safeIndex === list.length - 1}
              aria-label="Next image"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <span className="fa-solid fa-chevron-right"></span>
            </IconButton>
          )}
        </Box>
      </CardContent>

      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <Box
          sx={{
            position: 'relative',
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', minWidth: 44, minHeight: 44 }}
          >
            <span className="fa-solid fa-xmark"></span>
          </IconButton>
          {hasMultiple && safeIndex > 0 && (
            <IconButton
              onClick={showPrev}
              aria-label="Previous image"
              sx={{ position: 'absolute', left: 8, color: '#fff', minWidth: 44, minHeight: 44 }}
            >
              <span className="fa-solid fa-chevron-left"></span>
            </IconButton>
          )}
          {isBroken ? (
            <Box sx={{ color: '#fff', textAlign: 'center' }}>
              <Typography variant="body2">Image unavailable</Typography>
            </Box>
          ) : (
            <Box
              component="img"
              src={current.url}
              alt={current.caption || `Sample image ${safeIndex + 1}`}
              onError={() => handleImageError(current.key)}
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
          {hasMultiple && safeIndex < list.length - 1 && (
            <IconButton
              onClick={showNext}
              aria-label="Next image"
              sx={{ position: 'absolute', right: 8, color: '#fff', minWidth: 44, minHeight: 44 }}
            >
              <span className="fa-solid fa-chevron-right"></span>
            </IconButton>
          )}
        </Box>
      </Dialog>
    </Card>
  )
}

export default SampleImageGrid
